import React, { useState, useEffect } from 'react';
import { X, Plus, Clock, Instagram, Calendar, Target, Hash, Zap, Trash2, Palette, ChevronDown, CheckSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useOplaes } from '@/providers/OplaesProvider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import ColorPicker from '@/components/ui/ColorPicker';

const BRAND_COLORS = [
  { name: 'Periwinkle', value: '#818cf8' },   // Soft indigo
  { name: 'Lavender', value: '#a78bfa' },     // Soft purple
  { name: 'Rose', value: '#f472b6' },         // Soft pink
  { name: 'Sky', value: '#60a5fa' },          // Soft blue
  { name: 'Aqua', value: '#67e8f9' },         // Soft cyan
  { name: 'Mint', value: '#5eead4' },         // Soft teal
  { name: 'Sage', value: '#86efac' },         // Soft green
  { name: 'Peach', value: '#fdba74' },        // Soft orange
  { name: 'Coral', value: '#fca5a5' },        // Soft red
  { name: 'Lilac', value: '#c4b5fd' },        // Soft violet
];

const STRATEGIES = [
  { 
    id: 'peak-hours', 
    name: 'Peak Hours', 
    description: 'Post during highest engagement times to maximize reach and visibility'
  },
  { 
    id: 'engage-post-engage', 
    name: 'Engage-Post-Engage', 
    description: 'Engage with your audience before and after posting to boost engagement'
  }
];

const CADENCES = [
  { id: 'daily', name: 'Daily', description: 'Post content every day' },
  { id: 'weekly', name: 'Weekly', description: 'Post content every week' },
  { id: 'monthly', name: 'Monthly', description: 'Post content every month' }
];

const BuildFromScratchModal = ({ isOpen, onClose }) => {
  const { createOplae, loading: isCreating, error } = useOplaes();
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    color: '#818cf8',
    strategy: null,
    active: true,
    goal: null,
    tasks: []
  });

  const [expandedSections, setExpandedSections] = useState({
    strategy: false,
    goals: false,
    tasks: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleStrategySelect = (strategyId) => {
    setFormData(prev => ({
      ...prev,
      strategy: prev.strategy === strategyId ? null : strategyId
    }));
  };

  const handleCadenceSelect = (cadenceId) => {
    if (!formData.goal) {
      // If no goal exists, create one with current local date and time
      const now = new Date();
      // Format the date in local timezone with YYYY-MM-DDTHH:mm:ss format
      const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
        .toISOString()
        .slice(0, 19);
      
      setFormData(prev => ({
        ...prev,
        goal: {
          cadence: cadenceId,
          start_date: localDateTime,
          videos_count: 2
        }
      }));
    } else if (formData.goal.cadence === cadenceId) {
      // If same cadence is selected, remove the entire goal
      setFormData(prev => ({
        ...prev,
        goal: null
      }));
    } else {
      // If different cadence is selected, update the cadence
      setFormData(prev => ({
        ...prev,
        goal: {
          ...prev.goal,
          cadence: cadenceId
        }
      }));
    }
  };

  const handleAddTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        {
          title: '',
          goal: 1,
          query: { type: 'hashtag', value: '' }
        }
      ]
    }));
  };

  const handleRemoveTask = (index) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateTask = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) => 
        i === index 
          ? field.includes('query.') 
            ? { ...task, query: { ...task.query, [field.split('.')[1]]: value } }
            : { ...task, [field]: value }
          : task
      )
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) return;

    try {
      // Clean up the form data before submission
      const submitData = {
        ...formData,
        // Only include non-null values
        ...(formData.strategy && { strategy: formData.strategy }),
        ...(formData.goal && { goal: formData.goal }),
        ...(formData.tasks.length > 0 && { tasks: formData.tasks })
      };

      await createOplae(submitData);
      onClose();
    } catch (err) {
      console.error('Failed to create oplae:', err);
    }
  };

  const handleModalClose = (e) => {
    if (!isColorPickerOpen) {
      onClose(e);
    }
  };

  const colorTriggerRef = React.useRef(null);

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent className="!rounded-2xl sm:max-w-[800px] w-[calc(100%-32px)] p-0 
                               bg-gradient-to-br from-gray-900/90 to-gray-800/90 
                               border border-gray-800/50 overflow-hidden"
                    data-color-picker-modal="true"
                    onPointerDownOutside={(e) => {
                      // Allow events to reach color picker
                      if (e.target.closest('[data-color-picker="true"]')) {
                        e.preventDefault();
                      }
                    }}>
        <div className="relative rounded-2xl overflow-hidden">
          {/* Top Bar */}
          <div className="px-4 py-3 bg-gradient-to-r from-gray-900/80 to-gray-800/80 
                         backdrop-blur-sm border-b border-gray-700/30
                         flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-dashed border-gray-700/50 
                            flex items-center justify-center
                            bg-gradient-to-br from-gray-800/30 to-gray-700/30">
                <Plus className="w-4 h-4 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100">
                Create New Oplae
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Content */}
          <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Required Fields */}
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      name: e.target.value
                    }))}
                    placeholder="e.g., Growth Strategy 2024"
                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                              rounded-xl text-gray-100 placeholder-gray-500
                              focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  />
                </div>

                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Color
                  </label>
                  <div className={cn(
                    "relative",
                    isColorPickerOpen ? "mb-[450px]" : "min-h-[40px]"
                  )}>
                    <button
                      onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                      className={cn(
                        "w-10 h-10 rounded-full transition-all duration-200",
                        "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 ring-offset-gray-900",
                        "ring-2 ring-offset-2",
                        "absolute right-0 top-0"
                      )}
                      style={{ backgroundColor: formData.color }}
                      title="Choose color"
                    />
                    {isColorPickerOpen && (
                      <ColorPicker
                        color={formData.color}
                        onChange={(color) => {
                          setFormData(prev => ({ ...prev, color }));
                          setIsColorPickerOpen(false);
                        }}
                        onClose={() => setIsColorPickerOpen(false)}
                        onColorChange={(color) => {
                          setFormData(prev => ({ ...prev, color }));
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Optional Sections */}
            <div className="space-y-4 pt-4 border-t border-gray-800">
              {/* Goals Section */}
              <div className="rounded-xl border border-gray-800/50 overflow-hidden">
                <button
                  onClick={() => toggleSection('goals')}
                  className="w-full px-4 py-3 bg-gray-800/30 
                           flex items-center justify-between
                           hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-blue-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-200">Goals</span>
                      {formData.goal && (
                        <span className="ml-2 text-xs text-gray-400">
                          ({CADENCES.find(c => c.id === formData.goal.cadence)?.name})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.goal && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, goal: null }));
                        }}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className={cn(
                      "transform transition-transform",
                      expandedSections.goals ? "rotate-180" : ""
                    )}>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
                {expandedSections.goals && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {CADENCES.map((cadence) => (
                        <button
                          key={cadence.id}
                          onClick={() => handleCadenceSelect(cadence.id)}
                          className={cn(
                            "flex flex-col items-center p-4",
                            "rounded-xl border transition-all duration-200",
                            "hover:bg-gray-800/50",
                            formData.goal?.cadence === cadence.id
                              ? "border-purple-500/50 bg-purple-500/10"
                              : "border-gray-700/50 bg-gray-800/30"
                          )}
                        >
                          <span className="text-gray-100 font-medium text-center">{cadence.name}</span>
                          <span className="text-xs text-gray-400 mt-1 text-center">{cadence.description}</span>
                        </button>
                      ))}
                    </div>

                    {formData.goal && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Start Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            value={formData.goal.start_date}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              goal: { ...prev.goal, start_date: e.target.value }
                            }))}
                            step="1"
                            className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                    rounded-xl text-gray-100
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Videos per {formData.goal.cadence === 'daily' ? 'Day' : 'Week'}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={formData.goal.videos_count}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              goal: { ...prev.goal, videos_count: parseInt(e.target.value) }
                            }))}
                            className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                    rounded-xl text-gray-100
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Strategy Section */}
              <div className="rounded-xl border border-gray-800/50 overflow-hidden">
                <button
                  onClick={() => toggleSection('strategy')}
                  className="w-full px-4 py-3 bg-gray-800/30 
                           flex items-center justify-between
                           hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-purple-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-200">Strategy</span>
                      {formData.strategy && (
                        <span className="ml-2 text-xs text-gray-400">
                          ({STRATEGIES.find(s => s.id === formData.strategy)?.name})
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.strategy && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, strategy: null, tasks: [] }));
                        }}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className={cn(
                      "transform transition-transform",
                      expandedSections.strategy ? "rotate-180" : ""
                    )}>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
                {expandedSections.strategy && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {STRATEGIES.map((strategy) => (
                        <button
                          key={strategy.id}
                          onClick={() => handleStrategySelect(strategy.id)}
                          className={cn(
                            "flex flex-col items-center p-4",
                            "rounded-xl border transition-all duration-200",
                            "hover:bg-gray-800/50",
                            formData.strategy === strategy.id
                              ? "border-purple-500/50 bg-purple-500/10"
                              : "border-gray-700/50 bg-gray-800/30"
                          )}
                        >
                          <span className="text-gray-100 font-medium text-center">{strategy.name}</span>
                          <span className="text-xs text-gray-400 mt-1 text-center">{strategy.description}</span>
                        </button>
                      ))}
                    </div>

                    {/* Tasks Section - Only show for Engage-Post-Engage strategy */}
                    {formData.strategy === 'engage-post-engage' && (
                      <div className="mt-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckSquare className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-medium text-gray-200">Engagement Tasks</span>
                          </div>
                          <Button
                            onClick={handleAddTask}
                            className="bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 text-xs"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Task
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          {formData.tasks.map((task, index) => (
                            <div 
                              key={index}
                              className="p-4 bg-gray-800/30 border border-gray-700/50 rounded-xl space-y-3"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={task.title}
                                    onChange={(e) => handleUpdateTask(index, 'title', e.target.value)}
                                    placeholder="e.g., Comment on #growthhacking posts"
                                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                             rounded-xl text-gray-100 placeholder-gray-500
                                             focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                  />
                                </div>
                                <Button
                                  variant="ghost"
                                  onClick={() => handleRemoveTask(index)}
                                  className="text-gray-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">
                                    Goal Count
                                  </label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={task.goal}
                                    onChange={(e) => handleUpdateTask(index, 'goal', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                             rounded-xl text-gray-100
                                             focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">
                                    Query Type
                                  </label>
                                  <select
                                    value={task.query.type}
                                    onChange={(e) => handleUpdateTask(index, 'query.type', e.target.value)}
                                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                             rounded-xl text-gray-100
                                             focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                  >
                                    <option value="hashtag">Hashtag</option>
                                    <option value="keyword">Keyword</option>
                                    <option value="creator">Creator</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">
                                    Query Value
                                  </label>
                                  <input
                                    type="text"
                                    value={task.query.value}
                                    onChange={(e) => handleUpdateTask(index, 'query.value', e.target.value)}
                                    placeholder={`Enter ${task.query.type}...`}
                                    className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700/50 
                                             rounded-xl text-gray-100 placeholder-gray-500
                                             focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 
                         backdrop-blur-sm border-t border-gray-700/30">
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-400">Estimated setup: 2-3 min</span>
                {error && (
                  <span className="text-sm text-red-400 ml-2">{error}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="text-gray-300 hover:text-gray-100"
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  className={cn(
                    "px-6 py-2 text-sm text-white rounded-xl",
                    "bg-gradient-to-r from-purple-500 to-pink-500",
                    "hover:from-purple-600 hover:to-pink-600",
                    "transition-all duration-200",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                  disabled={!formData.name || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create Oplae'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BuildFromScratchModal; 